import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  deriveLibraryTitle,
  getLibrarySourceTypeLabel,
  type LibraryItem,
  type LibraryItemStatus,
} from "@/lib/library";
import { LibraryStatusMenu } from "@/components/library/LibraryStatusMenu";
import { OpenAccessLink } from "@/components/library/OpenAccessLink";
import { getSourceRowToneVars, type SourceRowToneVars } from "@/lib/sourceTypeRowTone";
import { getSourceTypeIcon } from "@/lib/sourceTypeIcons";
import { BookOpenText, Languages, PencilLine } from "lucide-react";

type LibraryItemWithCategories = LibraryItem & {
  categories?: Array<{ id: string; name: string; color: string }>;
  excerptCount?: number;
};

const getYear = (item: LibraryItem): string | null => {
  if (item.published_year) return String(item.published_year);
  if (item.published_date) {
    const match = item.published_date.match(/\d{4}/);
    if (match) return match[0];
  }
  return null;
};

/** Outlined chip styling: bold text in title tone, border matches the icon-badge accent (tone.selected). */
const getChipStyle = (tone: SourceRowToneVars): CSSProperties => ({
  backgroundColor: "transparent",
  color: tone.title,
  borderColor: tone.selected,
  borderWidth: 1,
});

export const LibraryItemCard = ({
  item,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
  onEdit,
  onCreateExcerpt,
  onView,
  onChangeStatus,
  statusBusy = false,
}: {
  item: LibraryItemWithCategories;
  selectMode?: boolean;
  isSelected?: boolean;
  /** Deprecated; kept so existing call sites compile. */
  draggable?: boolean;
  onToggleSelect?: (itemId: string) => void;
  onEdit: (item: LibraryItemWithCategories) => void;
  onCreateExcerpt: (item: LibraryItemWithCategories) => void;
  onView: (itemId: string) => void;
  onChangeStatus?: (itemId: string, status: LibraryItemStatus) => void | Promise<void>;
  statusBusy?: boolean;
  /** Deprecated; the dedicated Excerpts button was removed from the card. */
  onViewExcerpts?: (item: LibraryItemWithCategories) => void;
}) => {
  const title = deriveLibraryTitle(item);
  const year = getYear(item);
  const author = item.author_text || "Unknown author";
  const sourceType = item.source_type ?? "other";
  const tone = getSourceRowToneVars(sourceType);
  const SourceIcon = getSourceTypeIcon(sourceType);
  const chipStyle = getChipStyle(tone);
  const [hovered, setHovered] = useState(false);
  const titleRef = useRef<HTMLSpanElement | null>(null);
  const [titleWraps, setTitleWraps] = useState(false);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight || "0");
      if (!lineHeight) return;
      setTitleWraps(el.getBoundingClientRect().height > lineHeight * 1.5);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [title]);

  const metaParts = [author, year].filter(Boolean) as string[];
  const sep = <span className="mx-1.5">·</span>;

  const metaInline = metaParts.length ? (
    <span className="text-base font-normal" style={{ color: tone.meta }}>
      {metaParts.map((part, idx) => (
        <span key={idx}>
          {idx > 0 ? sep : null}
          {part}
        </span>
      ))}
    </span>
  ) : null;

  const inner = (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? tone.hover : tone.idle,
        borderLeft: `4px solid ${tone.accent}`,
      }}
      className={`relative w-full shadow-soft transition-shadow ${
        selectMode && isSelected ? "ring-2 ring-primary" : ""
      }`}
    >
      <div className="flex items-start gap-4 p-4">
        {selectMode ? (
          <div className="flex items-center self-start pt-1">
            <Checkbox
              checked={isSelected}
              className="shrink-0"
              onCheckedChange={() => onToggleSelect?.(item.id)}
              onClick={(event) => event.stopPropagation()}
              aria-label={`Select ${title}`}
            />
          </div>
        ) : null}

        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-md border"
          style={{ backgroundColor: tone.selected, borderColor: tone.selected, color: tone.title }}
          aria-hidden="true"
          title={getLibrarySourceTypeLabel(sourceType)}
        >
          <SourceIcon className="h-5 w-5" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {(() => {
            const chipClass =
              "inline-flex items-center rounded-full px-2 py-0.5 align-middle text-xs font-semibold";
            const chips = (
              <>
                {" "}
                <span className={chipClass} style={chipStyle}>
                  {getLibrarySourceTypeLabel(sourceType)}
                </span>
                {item.language_code ? (
                  <>
                    {" "}
                    <span className={chipClass} style={chipStyle}>
                      <Languages className="mr-1 h-3 w-3" />
                      {item.language_code.toUpperCase()}
                    </span>
                  </>
                ) : null}
                {" "}
                {onChangeStatus && !selectMode ? (
                  <LibraryStatusMenu
                    status={item.status}
                    onChange={(next) => onChangeStatus(item.id, next)}
                    disabled={statusBusy}
                    triggerStyle={chipStyle}
                  />
                ) : (
                  <span className={chipClass} style={chipStyle}>
                    {item.status === "to_read" ? "To read" : item.status === "reading" ? "Reading" : item.status === "read" ? "Read" : "Revisit"}
                  </span>
                )}
                {sourceType === "book" && (item as LibraryItem & { is_owned?: boolean }).is_owned ? (
                  <>
                    {" "}
                    <span className={chipClass} style={chipStyle} title="In your physical library">
                      Owned
                    </span>
                    {(item as LibraryItem & { book_binding?: string | null }).book_binding ? (
                      <>
                        {" "}
                        <span className={chipClass} style={chipStyle}>
                          {(item as LibraryItem & { book_binding?: string | null }).book_binding === "hardback" ? "Hardback" : "Paperback"}
                        </span>
                      </>
                    ) : null}
                    {(item as LibraryItem & { is_first_edition?: boolean }).is_first_edition ? (
                      <>
                        {" "}
                        <span className={chipClass} style={chipStyle}>First edition</span>
                      </>
                    ) : null}
                    {(item as LibraryItem & { is_numbered?: boolean }).is_numbered ? (
                      <>
                        {" "}
                        <span className={chipClass} style={chipStyle}>
                          {(() => {
                            const n = (item as LibraryItem & { numbered_index?: number | null }).numbered_index;
                            const t = (item as LibraryItem & { numbered_total?: number | null }).numbered_total;
                            return n != null && t != null ? `Numbered ${n}/${t}` : "Numbered";
                          })()}
                        </span>
                      </>
                    ) : null}
                    {(item as LibraryItem & { is_signed?: boolean }).is_signed ? (
                      <>
                        {" "}
                        <span className={chipClass} style={chipStyle}>Signed</span>
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            );
            return (
              <>
                <h2 className="text-lg leading-snug" style={{ color: tone.title }}>
                  <span ref={titleRef} className="font-semibold">{title}</span>
                  {titleWraps && metaInline ? (
                    <>
                      {sep}
                      {metaInline}
                      {chips}
                    </>
                  ) : null}
                  {titleWraps && !metaInline ? chips : null}
                </h2>
                {!titleWraps && metaInline ? (
                  <p className="text-base leading-snug" style={{ color: tone.meta }}>
                    {metaInline}
                    {chips}
                  </p>
                ) : null}
                {!titleWraps && !metaInline ? (
                  <p className="text-base leading-snug">{chips}</p>
                ) : null}
              </>
            );
          })()}

          {item.categories?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {item.categories.map((category) => (
                <span
                  key={category.id}
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                  style={chipStyle}
                >
                  <span className="truncate">{category.name}</span>
                </span>
              ))}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => onCreateExcerpt(item)}
              disabled={selectMode}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={chipStyle}
            >
              <BookOpenText className="h-3 w-3" /> New excerpt
            </button>
            <button
              type="button"
              onClick={() => onEdit(item)}
              disabled={selectMode}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={chipStyle}
            >
              <PencilLine className="h-3 w-3" /> Edit
            </button>
            <button
              type="button"
              onClick={() => onView(item.id)}
              disabled={selectMode}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={chipStyle}
            >
              View
            </button>
            <OpenAccessLink href={(item as { open_access_url?: string | null }).open_access_url} />
          </div>
        </div>
      </div>
    </Card>
  );

  if (!selectMode) return inner;

  return (
    <div className="cursor-pointer" onClick={() => onToggleSelect?.(item.id)}>
      {inner}
    </div>
  );
};
